'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  
  // Set mounted state
  useEffect(() => {
    setHasMounted(true);
  }, []);
  
  // Handle scroll effect - only run client-side
  useEffect(() => {
    if (!hasMounted) return;
    
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMounted]);
  
  // Handle close when clicking outside mobile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen && 
        mobileMenuRef.current && 
        !mobileMenuRef.current.contains(event.target as Node) &&
        menuButtonRef.current && 
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  
  const toggleMobileDropdown = (name: string) => {
    setActiveMobileDropdown(prev => prev === name ? null : name);
  };
  
  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setActiveMobileDropdown(null);
  };
  
  return (
    <header className={`sticky top-0 z-50 bg-white ${scrolled ? 'shadow-lg py-2 transition-all duration-300' : 'shadow-md py-4 transition-all duration-300'}`} itemScope itemType="https://schema.org/WPHeader">
      <div className="container-custom">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link href="/" className="flex items-center relative z-10" itemProp="url" aria-label="Page d'accueil du Cabinet Marie-Camille Roch">
            <Image 
              src="/images/Logo-MCR_Avocate.png" 
              alt="Maître Marie-Camille Roch - Avocate à Montpellier" 
              width={150} 
              height={60} 
              className="h-auto w-[120px] md:w-[150px]"
              itemProp="image"
            />
          </Link>
          
          {/* Mobile: appel direct + menu button */}
          <div className="md:hidden flex items-center gap-1">
            <a
              href="tel:+33608357017"
              className="flex items-center justify-center w-11 h-11 rounded-md bg-primary text-white shadow-sm hover:bg-primary/90 transition-colors duration-300"
              aria-label="Appeler le cabinet : 06 08 35 70 17"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
            </a>
            <button
              ref={menuButtonRef}
              className="flex items-center justify-center z-[60] w-11 h-11 focus:outline-none relative"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileMenuOpen}
            >
              <div className="relative w-6 h-5">
                <span className={`absolute h-0.5 w-6 bg-primary rounded-sm transform transition duration-300 ease-in-out ${mobileMenuOpen ? 'rotate-45 translate-y-2.5' : ''}`}></span>
                <span className={`absolute h-0.5 bg-primary rounded-sm transform transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'w-0 opacity-0' : 'w-6 opacity-100'} top-2`}></span>
                <span className={`absolute h-0.5 w-6 bg-primary rounded-sm transform transition duration-300 ease-in-out ${mobileMenuOpen ? '-rotate-45 translate-y-2.5' : 'translate-y-4'}`}></span>
              </div>
            </button>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" itemScope itemType="https://schema.org/SiteNavigationElement">
            <Link href="/" className="text-gray-800 hover:text-primary font-medium relative group transition-colors duration-300" itemProp="url">
              <span className="uppercase" itemProp="name">Accueil</span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Droit Pénal Dropdown */}
            <div className="group relative">
              <div className="text-gray-800 hover:text-primary font-medium flex items-center relative transition-colors duration-300 group">
                <Link href="/services/droit-penal" className="uppercase flex items-center" itemProp="url">
                  <span itemProp="name">Droit Pénal</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1 transform group-hover:rotate-180 transition-transform duration-300" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </Link>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </div>
              <div className="dropdown-menu rounded-md shadow-lg overflow-hidden opacity-0 invisible absolute transform -translate-y-2 left-0 mt-0 min-w-[250px] bg-white transition-all duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 before:block before:h-2 before:absolute before:w-full before:-top-2">
                <Link href="/services/droit-penal#audition-libre" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Assistance en audition libre</span>
                </Link>
                <Link href="/services/droit-penal#garde-a-vue" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Assistance en garde à vue</span>
                </Link>
                <Link href="/services/droit-penal#instruction" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Assistance durant une information judiciaire</span>
                </Link>
                <Link href="/services/droit-penal#juridictions" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Assistance devant une juridiction correctionnelle</span>
                </Link>
                <Link href="/services/droit-penal#juridictions" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Assistance devant une juridiction criminelle</span>
                </Link>
                <Link href="/services/droit-penal#amenagement" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Aménagements de peine</span>
                </Link>
              </div>
            </div>
            
            {/* Effacement de Fichiers Dropdown */}
            <div className="group relative">
              <div className="text-gray-800 hover:text-primary font-medium flex items-center relative transition-colors duration-300 group">
                <Link href="/services/effacement-fichiers" className="uppercase flex items-center" itemProp="url">
                  <span itemProp="name">Effacement de Fichiers</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1 transform group-hover:rotate-180 transition-transform duration-300" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </Link>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </div>
              <div className="dropdown-menu rounded-md shadow-lg overflow-hidden opacity-0 invisible absolute transform -translate-y-2 left-0 mt-0 min-w-[250px] bg-white transition-all duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 before:block before:h-2 before:absolute before:w-full before:-top-2">
                <Link href="/services/effacement-fichiers#casier" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Casier judiciaire</span>
                </Link>
                <Link href="/services/taj" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Fichier TAJ</span>
                </Link>
              </div>
            </div>
            
            {/* Dommage Corporel */}
            <Link href="/services/dommages-corporels" className="text-gray-800 hover:text-primary font-medium relative group transition-colors duration-300" itemProp="url">
              <span className="uppercase" itemProp="name">Dommages Corporels</span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Blog */}
            <Link href="/blog" className="text-gray-800 hover:text-primary font-medium relative group transition-colors duration-300" itemProp="url">
              <span className="uppercase" itemProp="name">Blog</span>
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {/* Le Cabinet Dropdown */}
            <div className="group relative">
              <div className="text-gray-800 hover:text-primary font-medium flex items-center relative transition-colors duration-300 group">
                <Link href="/a-propos" className="uppercase flex items-center" itemProp="url">
                  <span itemProp="name">Le Cabinet</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1 transform group-hover:rotate-180 transition-transform duration-300" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </Link>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </div>
              <div className="dropdown-menu rounded-md shadow-lg overflow-hidden opacity-0 invisible absolute transform -translate-y-2 left-0 mt-0 min-w-[250px] bg-white transition-all duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 before:block before:h-2 before:absolute before:w-full before:-top-2">
                <Link href="/a-propos" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">À propos du cabinet</span>
                </Link>
                <Link href="/honoraires" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Nos honoraires</span>
                </Link>
                <Link href="/contact" className="dropdown-item hover:bg-gray-50 hover:pl-6 transition-all duration-300" itemProp="url">
                  <span itemProp="name">Contactez-nous</span>
                </Link>
              </div>
            </div>
            
            {/* Contact Button */}
            <Link
              href="/contact"
              className="text-white bg-primary hover:bg-primary/90 font-medium px-6 py-2.5 rounded-md transition-all duration-300 uppercase shadow-sm hover:shadow-md"
              itemProp="url"
            >
              <span itemProp="name">Contactez-nous</span>
            </Link>
          </nav>
        </div>
        
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
        )}
        
        {/* Mobile Navigation */}
        <div 
          ref={mobileMenuRef}
          className={`md:hidden fixed top-0 right-0 bottom-0 z-50 w-[80%] max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out overflow-y-auto ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
          aria-modal={mobileMenuOpen}
          role="dialog"
          aria-label="Menu de navigation"
          itemScope
          itemType="https://schema.org/SiteNavigationElement"
        >
          <div className="pt-24 pb-8 px-6">
            <nav className="flex flex-col">
              <Link 
                href="/" 
                className="text-gray-800 hover:text-primary font-medium py-3 border-b border-gray-100 flex items-center"
                onClick={handleLinkClick}
                itemProp="url"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
                <span itemProp="name">ACCUEIL</span>
              </Link>
              
              {/* Droit Pénal Dropdown */}
              <div className="border-b border-gray-100">
                <button 
                  className="text-gray-800 hover:text-primary font-medium flex items-center w-full justify-between py-3"
                  onClick={() => toggleMobileDropdown('droit-penal')}
                  aria-expanded={activeMobileDropdown === 'droit-penal'}
                >
                  <Link 
                    href="/services/droit-penal" 
                    className="flex items-center"
                    onClick={handleLinkClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                    </svg>
                    DROIT PÉNAL
                  </Link>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className={`w-4 h-4 ml-1 transition-transform duration-300 ${activeMobileDropdown === 'droit-penal' ? 'rotate-180' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileDropdown('droit-penal');
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${activeMobileDropdown === 'droit-penal' ? 'max-h-[600px] py-2' : 'max-h-0'}`}
                  aria-hidden={activeMobileDropdown !== 'droit-penal'}
                >
                  <div className="pl-4 space-y-3">
                    <Link href="/services/droit-penal#audition-libre" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Assistance en audition libre
                    </Link>
                    <Link href="/services/droit-penal#garde-a-vue" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Assistance en garde à vue
                    </Link>
                    <Link href="/services/droit-penal#instruction" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Assistance durant une information judiciaire
                    </Link>
                    <Link href="/services/droit-penal#juridictions" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Assistance devant une juridiction correctionnelle
                    </Link>
                    <Link href="/services/droit-penal#juridictions" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Assistance devant une juridiction criminelle
                    </Link>
                    <Link href="/services/droit-penal#amenagement" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Aménagements de peine
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Effacement de Fichiers Dropdown */}
              <div className="border-b border-gray-100">
                <button 
                  className="text-gray-800 hover:text-primary font-medium flex items-center w-full justify-between py-3"
                  onClick={() => toggleMobileDropdown('effacement')}
                  aria-expanded={activeMobileDropdown === 'effacement'}
                >
                  <Link 
                    href="/services/effacement-fichiers" 
                    className="flex items-center"
                    onClick={handleLinkClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" />
                    </svg>
                    EFFACEMENT DE FICHIERS
                  </Link>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className={`w-4 h-4 ml-1 transition-transform duration-300 ${activeMobileDropdown === 'effacement' ? 'rotate-180' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileDropdown('effacement');
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${activeMobileDropdown === 'effacement' ? 'max-h-[600px] py-2' : 'max-h-0'}`}
                  aria-hidden={activeMobileDropdown !== 'effacement'}
                >
                  <div className="pl-4 space-y-3">
                    <Link href="/services/effacement-fichiers#casier" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Casier judiciaire
                    </Link>
                    <Link href="/services/taj" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Fichier TAJ
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Dommage Corporel */}
              <Link 
                href="/services/dommages-corporels" 
                className="text-gray-800 hover:text-primary font-medium py-3 border-b border-gray-100 flex items-center"
                onClick={handleLinkClick}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                DOMMAGES CORPORELS
              </Link>
              
              {/* Blog */}
              <Link 
                href="/blog" 
                className="text-gray-800 hover:text-primary font-medium py-3 border-b border-gray-100 flex items-center"
                onClick={handleLinkClick}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
                </svg>
                BLOG
              </Link>
              
              {/* Le Cabinet Dropdown */}
              <div className="border-b border-gray-100">
                <button 
                  className="text-gray-800 hover:text-primary font-medium flex items-center w-full justify-between py-3"
                  onClick={() => toggleMobileDropdown('cabinet')}
                  aria-expanded={activeMobileDropdown === 'cabinet'}
                >
                  <Link 
                    href="/a-propos" 
                    className="flex items-center"
                    onClick={handleLinkClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                    </svg>
                    LE CABINET
                  </Link>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className={`w-4 h-4 ml-1 transition-transform duration-300 ${activeMobileDropdown === 'cabinet' ? 'rotate-180' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileDropdown('cabinet');
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${activeMobileDropdown === 'cabinet' ? 'max-h-[600px] py-2' : 'max-h-0'}`}
                  aria-hidden={activeMobileDropdown !== 'cabinet'}
                >
                  <div className="pl-4 space-y-3">
                    <Link href="/a-propos" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      À propos du cabinet
                    </Link>
                    <Link href="/honoraires" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Nos honoraires
                    </Link>
                    <Link href="/contact" className="block text-gray-700 hover:text-primary py-2 pl-6 border-l-2 border-gray-100 hover:border-primary transition-colors duration-300" onClick={handleLinkClick}>
                      Contactez-nous
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Contact Button */}
              <div className="pt-6 space-y-3">
                <a
                  href="tel:+33608357017"
                  className="bg-primary text-white font-medium py-3.5 px-4 rounded-md flex items-center justify-center text-center shadow-sm hover:bg-primary/90 transition-colors duration-300"
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  APPELER : 06 08 35 70 17
                </a>
                <Link
                  href="/contact"
                  className="bg-primary text-white font-medium py-3.5 px-4 rounded-md flex items-center justify-center text-center shadow-sm hover:bg-primary/90 transition-colors duration-300"
                  onClick={handleLinkClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  CONTACTEZ-NOUS
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}
